import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Building2, Plus, Pencil, Trash2, Search, CheckCircle, XCircle } from 'lucide-react';
import { hasPermission } from '../lib/permissions';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ORG_TYPES = [
  'Guarda Municipal',
  'Polícia Ambiental',
  'Polícia Militar',
  'Secretaria do Meio Ambiente',
  'IBAMA',
  'Corpo de Bombeiros',
  'Particular',
  'Projeto/ONG',
  'Outro'
];

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);

  useEffect(() => {
    // Check if user has permission
    if (user && !hasPermission(user, 'organizations.manage')) {
      toast.error('Acesso restrito à administração do sistema');
      navigate('/');
      return;
    }
    fetchOrganizations();
  }, [user, showInactive]);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API}/api/organizations?active_only=${!showInactive}`);
      setOrganizations(response.data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Erro ao carregar organizações');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (org = null) => {
    if (org) {
      setEditingOrg(org);
      setFormName(org.name);
      setFormType(org.type);
    } else {
      setEditingOrg(null);
      setFormName('');
      setFormType('');
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOrg(null);
    setFormName('');
    setFormType('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formName || !formType) {
      toast.error('Nome e tipo são obrigatórios');
      return;
    }

    setSaving(true);
    try {
      if (editingOrg) {
        // Update
        await axios.put(`${API}/api/organizations/${editingOrg.id}`, {
          name: formName,
          type: formType
        });
        toast.success('Organização atualizada!');
      } else {
        // Create
        await axios.post(`${API}/api/organizations`, {
          name: formName,
          type: formType
        });
        toast.success('Organização criada!');
      }
      handleCloseModal();
      fetchOrganizations();
    } catch (error) {
      console.error('Error saving organization:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!orgToDelete) return;
    
    try {
      await axios.delete(`${API}/api/organizations/${orgToDelete.id}`);
      toast.success('Organização desativada');
      setShowDeleteConfirm(false);
      setOrgToDelete(null);
      fetchOrganizations();
    } catch (error) {
      console.error('Error deleting organization:', error);
      toast.error('Erro ao desativar organização');
    }
  };

  const handleReactivate = async (orgId) => {
    try {
      await axios.put(`${API}/api/organizations/${orgId}`, { active: true });
      toast.success('Organização reativada');
      fetchOrganizations();
    } catch (error) {
      console.error('Error reactivating organization:', error);
      toast.error('Erro ao reativar');
    }
  };

  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-12 w-12 object-contain"
                />
              )}
              <div className={SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}>
                <h1 className="text-xl font-bold text-emerald-800">Gestão de Organizações</h1>
                {SHOW_INSTITUTO_BW_BRANDING && (
                  <p className="text-xs text-emerald-500">Instituto BW - Sistema CRAS</p>
                )}
              </div>
            </div>
            <div className="flex space-x-2">
              <Button onClick={() => navigate('/')} variant="outline">Dashboard</Button>
              <Button onClick={logout} variant="ghost" className="text-red-600">Sair</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex-1 w-full sm:max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome ou tipo..."
                className="pl-10"
                data-testid="search-org-input"
              />
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => setShowInactive(!showInactive)}
              className={showInactive ? 'bg-gray-100' : ''}
            >
              {showInactive ? 'Ocultar Inativos' : 'Mostrar Inativos'}
            </Button>
            <Button
              onClick={() => handleOpenModal()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="new-org-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Organização
            </Button>
          </div>
        </div>

        {/* Organizations Grid */}
        {filteredOrgs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nenhuma organização encontrada</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredOrgs.map((org) => (
              <Card 
                key={org.id} 
                className={`border-l-4 ${org.active ? 'border-l-emerald-500' : 'border-l-gray-300 opacity-60'}`}
                data-testid={`org-card-${org.id}`}
              >
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className={`w-5 h-5 ${org.active ? 'text-emerald-600' : 'text-gray-400'}`} />
                        <h3 className="font-semibold text-lg">{org.name}</h3>
                      </div>
                      <Badge variant="outline" className="mb-2">
                        {org.type}
                      </Badge>
                      <div className="flex items-center gap-1 text-sm">
                        {org.active ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-green-600">Ativo</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-500">Inativo</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenModal(org)}
                        data-testid={`edit-org-btn-${org.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {org.active ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => {
                            setOrgToDelete(org);
                            setShowDeleteConfirm(true);
                          }}
                          data-testid={`delete-org-btn-${org.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-green-600"
                          onClick={() => handleReactivate(org.id)}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingOrg ? 'Editar Organização' : 'Nova Organização'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nome da Organização *</Label>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Ex: Guarda Municipal de São Paulo"
                  data-testid="org-name-input"
                />
              </div>
              <div>
                <Label>Tipo *</Label>
                <Select value={formType} onValueChange={setFormType}>
                  <SelectTrigger data-testid="org-type-select">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORG_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseModal}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-emerald-600">
                  {saving ? 'Salvando...' : (editingOrg ? 'Salvar' : 'Criar')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Desativação</DialogTitle>
            </DialogHeader>
            <p className="text-gray-600">
              Tem certeza que deseja desativar a organização <strong>{orgToDelete?.name}</strong>?
            </p>
            <p className="text-sm text-gray-500">
              A organização não será excluída permanentemente e poderá ser reativada depois.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Desativar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
